import React, { useState } from 'react';
import api from '../api/axios';
import { Mail, Loader2, ArrowLeft, CheckCircle2, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Appel à mon API backend
      await api.post('/auth/forgot-password', { email });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.message || "Une erreur est survenue. Vérifiez l'adresse email.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full bg-white rounded-[2.5rem] shadow-2xl shadow-blue-900/5 p-8 md:p-10 border border-slate-100"
      >
        {/* BOUTON RETOUR */}
        <div className="mb-8">
          <Link 
            to="/login" 
            className="inline-flex items-center text-slate-400 hover:text-blue-600 font-black text-[10px] uppercase tracking-widest transition-colors"
          >
            <ArrowLeft size={16} className="mr-2" /> Retour à la connexion
          </Link>
        </div>

        <AnimatePresence mode="wait">
          {!submitted ? (
            /* ÉTAPE 1 : FORMULAIRE Saisie Email */
            <motion.div
              key="form"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <div className="mb-8">
                <h2 className="text-2xl font-black text-slate-900 leading-tight">
                  Mot de passe <br /> 
                  <span className="text-blue-600">oublié ?</span>
                </h2>
                <p className="text-slate-400 font-medium text-sm mt-2">
                  Pas de panique ! Entrez votre email pour recevoir un lien de récupération.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="bg-red-50 text-red-600 p-4 rounded-2xl text-xs font-bold border border-red-100 flex items-center gap-2">
                    <AlertCircle size={16} />
                    {error}
                  </div>
                )}

                <div className="space-y-1.5">
                  <label className="text-[11px] font-black text-slate-400 uppercase ml-1">Email de récupération</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={20} />
                    <input 
                      type="email"
                      required
                      className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-transparent rounded-2xl focus:bg-white focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all font-bold text-slate-700 text-[16px]"
                      placeholder="nom@exemple.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                </div>

                <button 
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-black py-4 rounded-2xl shadow-xl shadow-blue-200 transition-all flex items-center justify-center space-x-2 disabled:opacity-70 active:scale-[0.98]"
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin" size={20} />
                      <span>Envoi en cours...</span>
                    </>
                  ) : (
                    <span>Recevoir le lien</span>
                  )}
                </button>
              </form>
            </motion.div>
          ) : (
            /* ÉTAPE 2 : MESSAGE DE SUCCÈS */
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-4"
            >
              <div className="w-20 h-20 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 size={40} />
              </div>
              <h2 className="text-2xl font-black text-slate-900 mb-2">Vérifiez vos emails</h2>
              <p className="text-slate-500 font-medium text-sm mb-8">
                Si un compte existe pour <span className="text-slate-900 font-bold">{email}</span>, vous recevrez un lien d'ici quelques instants.
              </p>
              
              <Link 
                to="/login"
                className="block w-full bg-slate-900 text-white font-black py-4 rounded-2xl shadow-lg hover:bg-slate-800 transition-all active:scale-[0.98]"
              >
                Retourner au Login
              </Link>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;